import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserRole } from '@kalachand/shared';
import { apiDelete, apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { IconButton } from '../../components/IconButton';
import { StatusPill } from '../../components/StatusPill';
import { Modal } from '../../components/Modal';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';
import { UserPermissionsModal } from './UserPermissionsModal';
import { Can } from '../../lib/permissions';
import { cn } from '../../lib/cn';

interface User {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  role: UserRole;
  homeShopId: string | null;
  homeShop: { id: string; code: string; name: string } | null;
  isActive: boolean;
  isTest: boolean;
  lastLoginAt: string | null;
  totpEnabledAt: string | null;
  // Currently-active shop assignments beyond the home shop (e.g. an ONLINE
  // grant alongside a branch home shop).
  assignments: { id: string; shopId: string }[];
}
interface Shop {
  id: string;
  code: string;
  name: string;
  type: 'HQ' | 'SHOP' | 'ONLINE';
}

// Strong random password using crypto, excluding ambiguous chars (0/O/1/l/I).
function generatePassword(len = 14): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join('');
}

const ROLES: UserRole[] = [
  'owner',
  'admin',
  'operations',
  'finance',
  'sales_girl',
  'viewer',
  'driver',
];
// Each role gets a colour code shown three ways: a solid dot/stripe (ROLE_DOT)
// and a soft tinted row background (ROLE_ROW) so a role is legible both at the
// row's leading edge and across the whole row. The filter bar above the table
// doubles as the legend.
const ROLE_DOT: Record<UserRole, string> = {
  owner: 'bg-accent',
  admin: 'bg-accent-2',
  operations: 'bg-info',
  finance: 'bg-warning',
  sales_girl: 'bg-positive',
  driver: 'bg-negative',
  viewer: 'bg-ink-4',
};
const ROLE_ROW: Record<UserRole, string> = {
  owner: 'bg-accent-soft',
  admin: 'bg-accent-soft/45',
  operations: 'bg-info-soft',
  finance: 'bg-warning-soft',
  sales_girl: 'bg-positive-soft',
  driver: 'bg-negative-soft/70',
  viewer: 'bg-paper-2',
};
const ROLE_LABEL: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  operations: 'Operations',
  finance: 'Finance',
  sales_girl: 'Sales',
  viewer: 'Viewer',
  driver: 'Driver',
};

// Compact relative-ish last-login (full timestamp on hover).
function shortLogin(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-MU', { day: 'numeric', month: 'short' });
}

export function UsersPage(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [permsFor, setPermsFor] = useState<string | null>(null);
  const [shopsFor, setShopsFor] = useState<User | null>(null);
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => apiGet<User[]>('/users') });
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => apiGet<Shop[]>('/shops') });

  const allUsers = useMemo(() => users.data ?? [], [users.data]);
  const roleCounts = useMemo(() => {
    const counts = Object.fromEntries(ROLES.map((r) => [r, 0])) as Record<UserRole, number>;
    for (const u of allUsers) counts[u.role] = (counts[u.role] ?? 0) + 1;
    return counts;
  }, [allUsers]);
  const visibleUsers =
    roleFilter === 'all' ? allUsers : allUsers.filter((u) => u.role === roleFilter);

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiPatch(`/users/${id}`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  // Flag / unflag a user as a sandbox test account. Gated by users.set_test_mode
  // server-side; the UI control is also wrapped in <Can> below.
  const toggleTest = useMutation({
    mutationFn: ({ id, isTest }: { id: string; isTest: boolean }) =>
      apiPatch(`/users/${id}`, { isTest }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const reset2fa = useMutation({
    mutationFn: (id: string) => apiPost(`/users/${id}/2fa/reset`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <PageScaffold
      scroll={false}
      title="Users"
      subtitle="Staff accounts, roles, shop assignments, custom permissions."
      actions={<Button onClick={() => setCreateOpen(true)}>+ Add user</Button>}
    >
      {/* Role filter — chips carry the role colour dot, so this bar is also the
          legend that decodes the row tints below. */}
      <div className="shrink-0 flex flex-wrap items-center gap-1.5 rounded-lg border border-rule bg-surface shadow-sm px-4 py-2.5">
        <span className="w-12 shrink-0 text-[10px] uppercase tracking-widest text-ink-4">Role</span>
        <RoleChip active={roleFilter === 'all'} onClick={() => setRoleFilter('all')}>
          All <span className="text-ink-4">· {allUsers.length}</span>
        </RoleChip>
        {ROLES.map((r) => (
          <RoleChip
            key={r}
            active={roleFilter === r}
            dot={ROLE_DOT[r]}
            onClick={() => setRoleFilter((cur) => (cur === r ? 'all' : r))}
          >
            {ROLE_LABEL[r]} <span className="text-ink-4">· {roleCounts[r] ?? 0}</span>
          </RoleChip>
        ))}
      </div>

      <DataTableCard
        footer={(shops.data ?? []).length > 0 ? <ShopsLegend shops={shops.data ?? []} /> : undefined}
      >
        <table className="w-full text-sm">
          <thead className={STICKY_HEAD}>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-y border-rule">
              <th className="w-1 p-0" aria-hidden />
              <th className="pl-5 pr-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">Shop</th>
              <th className="px-3 py-2 font-medium">Last login</th>
              <th className="px-3 py-2 font-medium text-center">2FA</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-sm text-ink-3">
                  No users with this role.
                </td>
              </tr>
            )}
            {visibleUsers.map((u) => (
              <tr
                key={u.id}
                className={cn(
                  'group border-b border-rule-2 transition hover:brightness-[0.97]',
                  ROLE_ROW[u.role],
                  !u.isActive && 'opacity-55',
                )}
              >
                {/* role colour stripe — see the filter/legend bar above */}
                <td className={cn('w-1 p-0', ROLE_DOT[u.role])} title={ROLE_LABEL[u.role]} />
                <td className="pl-5 pr-3 py-2.5">
                  <div className="text-ink font-medium leading-tight flex items-center gap-1.5">
                    {u.name}
                    {u.isTest && (
                      <span
                        className="inline-flex items-center rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700"
                        title="Test user — sandbox orders only"
                      >
                        Test
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-3 kc-mono truncate max-w-[230px]">{u.email}</div>
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5">
                    <span className={cn('w-2 h-2 rounded-full shrink-0', ROLE_DOT[u.role])} />
                    <span className="text-ink-2">{ROLE_LABEL[u.role]}</span>
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  {u.homeShop ? (
                    <span className="kc-mono text-xs text-ink-2" title={u.homeShop.name}>
                      {u.homeShop.code}
                    </span>
                  ) : (
                    <span className="text-ink-4">—</span>
                  )}
                </td>
                <td
                  className="px-3 py-2.5 text-xs text-ink-3 whitespace-nowrap"
                  title={
                    u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'never logged in'
                  }
                >
                  {shortLogin(u.lastLoginAt)}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {u.totpEnabledAt ? (
                    <span className="text-positive" title="Two-factor on">
                      ✓
                    </span>
                  ) : (
                    <span className="text-ink-4" title="Two-factor off">
                      –
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <StatusPill tone={u.isActive ? 'positive' : 'neutral'}>
                    {u.isActive ? 'active' : 'inactive'}
                  </StatusPill>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <IconButton
                      size="sm"
                      aria-label={`Edit ${u.name}`}
                      title="Edit"
                      onClick={() => setEditing(u)}
                    >
                      <PencilIcon />
                    </IconButton>
                    <IconButton
                      size="sm"
                      aria-label={`Reset password for ${u.name}`}
                      title="Reset password"
                      onClick={() => setResetting(u)}
                    >
                      <LockIcon />
                    </IconButton>
                    <Can permission="permissions.override">
                      <IconButton
                        size="sm"
                        aria-label={`Permissions for ${u.name}`}
                        title="Permissions"
                        onClick={() => setPermsFor(u.id)}
                      >
                        <KeyIcon />
                      </IconButton>
                    </Can>
                    <Can permission="users.assign_shop">
                      <IconButton
                        size="sm"
                        aria-label={`Shop access for ${u.name}`}
                        title="Shop access — additional shops beyond her home shop (e.g. online sales)"
                        onClick={() => setShopsFor(u)}
                      >
                        <ShopsAccessIcon />
                      </IconButton>
                    </Can>
                    <Can permission="users.set_test_mode">
                      <IconButton
                        size="sm"
                        aria-label={`${u.isTest ? 'Unset' : 'Set'} test user — ${u.name}`}
                        title={
                          u.isTest
                            ? 'Test user — orders never affect stock or accounts. Click to make a real user.'
                            : 'Make a test (sandbox) user'
                        }
                        className={u.isTest ? 'text-indigo-600' : undefined}
                        onClick={() => {
                          if (
                            window.confirm(
                              u.isTest
                                ? `Make ${u.name} a REAL user? New orders will hit stock and accounts. Existing test orders stay hidden.`
                                : `Make ${u.name} a TEST user? Their new orders will run the full flow without touching stock, accounts, commission, or reports.`,
                            )
                          ) {
                            toggleTest.mutate({ id: u.id, isTest: !u.isTest });
                          }
                        }}
                      >
                        <BeakerIcon />
                      </IconButton>
                    </Can>
                    {u.totpEnabledAt && (
                      <IconButton
                        size="sm"
                        aria-label={`Reset 2FA for ${u.name}`}
                        title="Reset 2FA"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Reset two-factor for ${u.name}? They'll re-enrol on next login.`,
                            )
                          ) {
                            reset2fa.mutate(u.id);
                          }
                        }}
                      >
                        <ResetIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="sm"
                      variant="danger"
                      aria-label={`${u.isActive ? 'Deactivate' : 'Reactivate'} ${u.name}`}
                      title={u.isActive ? 'Deactivate' : 'Reactivate'}
                      onClick={() => toggleActive.mutate({ id: u.id, isActive: !u.isActive })}
                    >
                      <PowerIcon />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableCard>

      {createOpen && (
        <UserModal mode="create" shops={shops.data ?? []} onClose={() => setCreateOpen(false)} />
      )}
      {editing && (
        <UserModal
          mode="edit"
          user={editing}
          shops={shops.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
      {permsFor && <UserPermissionsModal userId={permsFor} onClose={() => setPermsFor(null)} />}
      {shopsFor && (
        <UserShopAssignmentsModal
          user={shopsFor}
          shops={shops.data ?? []}
          onClose={() => setShopsFor(null)}
        />
      )}
    </PageScaffold>
  );
}

// Additional shops beyond the home shop (e.g. a branch seller also granted
// the ONLINE shop, or a second branch) — a checklist that diffs against the
// current UserShopAssignment rows and creates/deletes to match on save.
function UserShopAssignmentsModal({
  user,
  shops,
  onClose,
}: {
  user: User;
  shops: Shop[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  // Home shop is set on the Edit user form, not here — only additional shops
  // (temporary transfers, a second branch, ONLINE, etc.) are managed as
  // UserShopAssignment rows.
  const assignable = useMemo(
    () => shops.filter((s) => s.id !== user.homeShopId),
    [shops, user.homeShopId],
  );
  const initialIds = useMemo(
    () => new Set(user.assignments.map((a) => a.shopId)),
    [user.assignments],
  );
  const [checked, setChecked] = useState<Set<string>>(initialIds);

  const dirty = useMemo(() => {
    if (checked.size !== initialIds.size) return true;
    for (const id of checked) if (!initialIds.has(id)) return true;
    return false;
  }, [checked, initialIds]);

  const toggle = (shopId: string): void => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(shopId)) next.delete(shopId);
      else next.add(shopId);
      return next;
    });
  };

  const save = useMutation({
    mutationFn: async () => {
      const toAdd = [...checked].filter((id) => !initialIds.has(id));
      const toRemove = user.assignments.filter((a) => !checked.has(a.shopId));
      await Promise.all([
        ...toAdd.map((shopId) =>
          apiPost(`/users/${user.id}/shop-assignments`, {
            shopId,
            effectiveFrom: new Date().toISOString(),
          }),
        ),
        ...toRemove.map((a) => apiDelete(`/users/${user.id}/shop-assignments/${a.id}`)),
      ]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-rule flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-ink">Shop access</h3>
            <p className="text-xs text-ink-3 mt-0.5">
              {user.name} · home shop {user.homeShop?.code ?? 'none'} · applies on next login (or
              she can switch to it right away from her own "Currently at" menu once granted)
            </p>
          </div>
          <IconButton onClick={onClose} aria-label="Close">
            ×
          </IconButton>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {assignable.length === 0 ? (
            <p className="text-sm text-ink-3">No other shops to assign.</p>
          ) : (
            <div className="border border-rule rounded divide-y divide-rule">
              {assignable.map((s) => (
                <label
                  key={s.id}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-paper-2"
                >
                  <input
                    type="checkbox"
                    checked={checked.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-ink">
                    {s.type === 'ONLINE' ? s.name : `${s.code} · ${s.name}`}
                  </span>
                </label>
              ))}
            </div>
          )}
          {save.isError && (
            <div className="mt-3 text-xs text-negative">{getApiError(save.error).message}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-rule flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
            Save shop access
          </Button>
        </div>
      </div>
    </div>
  );
}

function UserModal({
  mode,
  user,
  shops,
  onClose,
}: {
  mode: 'create' | 'edit';
  user?: User;
  shops: Shop[];
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [role, setRole] = useState<UserRole>(user?.role ?? 'sales_girl');
  const [homeShopId, setHomeShopId] = useState<string>(user?.homeShopId ?? '');
  // After a successful create we show the credentials once (plaintext password
  // only exists here) so the admin can copy + hand them over.
  const [createdCreds, setCreatedCreds] = useState<{ email: string; password: string } | null>(
    null,
  );
  const qc = useQueryClient();

  const submit = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name,
        email,
        phone: phone || null,
        role,
        homeShopId: homeShopId || null,
      };
      if (mode === 'create') {
        body.password = password;
      }
      return mode === 'create' ? apiPost('/users', body) : apiPatch(`/users/${user!.id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      if (mode === 'create') setCreatedCreds({ email, password });
      else onClose();
    },
  });

  const disabled =
    mode === 'create' ? !name || !email || !password || password.length < 8 : !name || !email;

  // ── Credentials panel (shown once, right after a successful create) ──
  if (createdCreds) {
    return (
      <CredentialsShownOnce
        title="User created"
        subtitle="Share these credentials now — the password can't be shown again."
        email={createdCreds.email}
        password={createdCreds.password}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      title={mode === 'create' ? 'New user' : `Edit · ${user?.name}`}
      subtitle={
        mode === 'edit' ? 'Use “Reset password” on the row to set a new password.' : undefined
      }
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={submit.isPending} disabled={disabled} onClick={() => submit.mutate()}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        {mode === 'create' && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
              Password (8+ chars)
            </span>
            <div className="flex gap-2">
              <input
                type={showPw ? 'text' : 'password'}
                className="kc-input flex-1 kc-mono"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                placeholder="Enter or generate"
              />
              <Button
                variant="secondary"
                onClick={() => {
                  setPassword(generatePassword());
                  setShowPw(true);
                }}
              >
                Generate
              </Button>
              <Button variant="ghost" onClick={() => setShowPw((s) => !s)}>
                {showPw ? 'Hide' : 'Show'}
              </Button>
            </div>
            <span className="text-xs text-ink-3">
              You'll get a copy button after creating. The user can change it after first login.
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="kc-input"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
              Home shop
            </span>
            <select
              value={homeShopId}
              onChange={(e) => setHomeShopId(e.target.value)}
              className="kc-input"
            >
              <option value="">— none (HQ-level role) —</option>
              {shops.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {submit.isError && (
          <div className="text-xs text-negative">{getApiError(submit.error).message}</div>
        )}
      </div>
    </Modal>
  );
}

// Reset a user's password: admin sets/generates a new one, then it's shown once
// so they can hand it over. Resetting also terminates the user's live sessions.
function ResetPasswordModal({ user, onClose }: { user: User; onClose: () => void }): JSX.Element {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState<{ email: string; password: string } | null>(null);
  const qc = useQueryClient();

  const submit = useMutation({
    mutationFn: () => apiPost(`/users/${user.id}/reset-password`, { password }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setDone({ email: user.email, password });
    },
  });

  if (done) {
    return (
      <CredentialsShownOnce
        title="Password reset"
        subtitle="Share the new password now — it can't be shown again."
        email={done.email}
        password={done.password}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      title={`Reset password · ${user.name}`}
      subtitle="Sets a new password and signs the user out of all sessions immediately."
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submit.isPending}
            disabled={!password || password.length < 8}
            onClick={() => submit.mutate()}
          >
            Reset password
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
          New password (8+ chars)
        </span>
        <div className="flex gap-2">
          <input
            type={showPw ? 'text' : 'password'}
            className="kc-input flex-1 kc-mono"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            placeholder="Enter or generate"
          />
          <Button
            variant="secondary"
            onClick={() => {
              setPassword(generatePassword());
              setShowPw(true);
            }}
          >
            Generate
          </Button>
          <Button variant="ghost" onClick={() => setShowPw((s) => !s)}>
            {showPw ? 'Hide' : 'Show'}
          </Button>
        </div>
        <span className="text-xs text-ink-3">
          You'll get a copy button after resetting. Any active sessions for this user are ended.
        </span>
        {submit.isError && (
          <div className="mt-1 text-xs text-negative">{getApiError(submit.error).message}</div>
        )}
      </div>
    </Modal>
  );
}

// One-time credentials panel — the plaintext password only exists in the browser
// here, so the admin must copy + hand it over now. Shared by user-create and
// password-reset.
function CredentialsShownOnce({
  title,
  subtitle,
  email,
  password,
  onClose,
}: {
  title: string;
  subtitle: string;
  email: string;
  password: string;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (label: string, text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  };
  const both = `Email: ${email}\nPassword: ${password}`;
  return (
    <Modal
      title={title}
      subtitle={subtitle}
      size="lg"
      onClose={onClose}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="space-y-4">
        <div className="rounded-md border border-rule bg-paper-2 divide-y divide-rule-2">
          <CredRow
            label="Email"
            value={email}
            onCopy={() => copy('email', email)}
            copied={copied === 'email'}
          />
          <CredRow
            label="Password"
            value={password}
            mono
            onCopy={() => copy('password', password)}
            copied={copied === 'password'}
          />
        </div>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => copy('both', both)}>
            {copied === 'both' ? '✓ Copied' : 'Copy email + password'}
          </Button>
        </div>
        <p className="text-xs text-ink-3">
          The user can sign in with these now. Have them change the password after first login.
        </p>
      </div>
    </Modal>
  );
}

function CredRow({
  label,
  value,
  mono,
  onCopy,
  copied,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy: () => void;
  copied: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-ink-3">{label}</div>
        <div className={`text-sm text-ink truncate ${mono ? 'kc-mono' : ''}`}>{value}</div>
      </div>
      <Button size="sm" variant="ghost" onClick={onCopy}>
        {copied ? '✓ Copied' : 'Copy'}
      </Button>
    </div>
  );
}

// A filter chip that always shows its role colour dot, so the filter bar reads
// as the legend too. Clicking toggles the role filter (handled by the caller).
function RoleChip({
  active,
  dot,
  onClick,
  children,
}: {
  active: boolean;
  dot?: string;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition',
        active
          ? 'border-accent bg-accent-soft text-ink font-medium'
          : 'border-rule bg-paper-2 text-ink-2 hover:border-accent/40 hover:text-ink',
      )}
    >
      {dot && <span className={cn('w-2 h-2 rounded-full shrink-0', dot)} />}
      {children}
    </button>
  );
}

// Pinned footer reference for the shop codes used in the Shop column.
function ShopsLegend({ shops }: { shops: Shop[] }): JSX.Element {
  return (
    <div className="px-5 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-ink-3">
      <span className="font-medium uppercase tracking-wide text-ink-4">Shops</span>
      {shops.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-1">
          <span className="kc-mono text-ink-2">{s.code}</span>
          <span>{s.name}</span>
        </span>
      ))}
    </div>
  );
}

// ── Action icons (inline SVG, inherit currentColor from IconButton) ──────────
function BeakerIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 3h6" />
      <path d="M10 3v6.5L5 18a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 18l-5-8.5V3" />
      <path d="M7.5 14h9" />
    </svg>
  );
}
function PencilIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}
function LockIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
function ShopsAccessIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 9.5 5 4h14l2 5.5" />
      <path d="M3 9.5a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M10 20v-6h4v6" />
    </svg>
  );
}
function KeyIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M10.7 12.3 21 2" />
      <path d="M15.5 7.5l3 3" />
    </svg>
  );
}
function ResetIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 12a9 9 0 1 0 2.6-6.4L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}
function PowerIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2v10" />
      <path d="M6.4 6.4a8 8 0 1 0 11.2 0" />
    </svg>
  );
}
